import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { rollDigits } from "../helpers/digit_roll"
import { chipOver, centreOf, sparks, glow, shine, deltaOver } from "../helpers/leftover_fx"

// Last month's leftover going into the goal (goals/_leftover_strip), played on
// the dashboard instead of a reload. The POST goes out on the tap and the
// animation starts with it:
//
//   1. the amount lifts off the title as a chip and hovers;
//   2. the rail glides back one card to the goal strip under it (medium haptic);
//   3. the chip dives into the goal's water. At that moment the goal strip is
//      swapped for the server's fresh one, its water rises from the old level
//      to the new, its figures roll, sparks fly (success haptic);
//   4. the leftover strip leaves the rail (dismissible's sideways farewell).
//
// The new level and figures are always the server's: the response is the goal
// strip rendered fresh (goal_contributions/leftover.turbo_stream), never
// numbers worked out here. If the chip is ready to land before the answer is
// back it hovers until it is. If the server added nothing, the page reloads to
// the truth.
//
// The water rises by transform, never width: a width animation on the liquid
// rendered short and square-cornered in WKWebView (see .goal-fill-liquid).
//
// No goal strip beside it (no goal, or another layout) and the link is left to
// do its plain Turbo POST.
export default class extends Controller {
  static targets = ["link", "amount", "settleHaptic", "landHaptic"]

  static EASE_SPRING = "cubic-bezier(0.34, 1.45, 0.64, 1)"
  static EASE_FILL = "cubic-bezier(0.22, 1, 0.36, 1)"
  static GLIDE_MS = 650
  static FILL_MS = 1100
  static LIFT = "translateY(-34px) scale(1.3)"

  add(event) {
    if (this.busy) { event.preventDefault(); return }
    const goal = this.#goalStrip
    if (!goal) return

    event.preventDefault()
    this.busy = true
    const answer = this.#post()
    if (this.#reducedMotion) this.#quiet(goal, answer)
    else this.#play(goal, answer)
  }

  // The strip leaves the page before the last beat (step 4), so nothing after
  // #leave touches a target: the amount is read up front.
  async #play(goal, answer) {
    const amountHtml = this.amountTarget.innerHTML
    const chip = chipOver(this.amountTarget)
    this.amountTarget.style.visibility = "hidden"
    this.linkTarget.querySelectorAll(".wn-title, .wn-sub, .wn-seal").forEach((el) => {
      el.animate([{ opacity: 1 }, { opacity: 0.35 }], { duration: 300, fill: "forwards" })
    })
    chip.animate([{ transform: "translateY(0) scale(0.85)" }, { transform: this.constructor.LIFT }],
                 { duration: 480, easing: this.constructor.EASE_SPRING, fill: "forwards" })
    await this.#wait(420)

    // The chip lags a beat behind the rail, then catches up over the goal.
    chip.animate([
      { transform: this.constructor.LIFT },
      { transform: `translate(${this.#rtl ? 22 : -22}px, -40px) scale(1.3) rotate(${this.#rtl ? 4 : -4}deg)`, offset: 0.55 },
      { transform: this.constructor.LIFT }
    ], { duration: this.constructor.GLIDE_MS, easing: "ease-in-out", fill: "forwards" })
    await this.#glideTo(goal)
    this.#haptic(this.settleHapticTarget)
    await this.#wait(120)

    const hover = chip.animate([
      { transform: this.constructor.LIFT },
      { transform: "translateY(-38px) scale(1.3)" },
      { transform: this.constructor.LIFT }
    ], { duration: 900, iterations: Infinity, easing: "ease-in-out" })
    const fresh = await answer
    hover.cancel()
    if (!fresh) return this.#recover(chip)

    const target = this.#waterEdge(goal)
    await this.#dive(chip, target)
    chip.remove()

    const rtl = this.#rtl
    const live = this.#land(goal, fresh, target)
    await this.#wait(900)
    this.#leave()
    await this.#wait(this.constructor.FILL_MS - 900 + 60)
    shine(live)
    deltaOver(live, amountHtml, rtl)
  }

  // Reduced motion: no flight, no rise. The goal strip changes in place and
  // the leftover strip fades out.
  async #quiet(goal, answer) {
    const fresh = await answer
    if (!fresh) return this.#recover(null)
    this.#swapAll(goal, fresh)
    this.#leave()
  }

  #post() {
    const token = document.querySelector("meta[name='csrf-token']")?.content
    return fetch(this.linkTarget.href, {
      method: "POST",
      credentials: "same-origin",
      headers: { "X-CSRF-Token": token, Accept: "text/vnd.turbo-stream.html" }
    }).then(async (response) => (response.ok ? this.#stripFrom(await response.text()) : null), () => null)
  }

  // The fresh goal strip, lifted out of the stream's template.
  #stripFrom(html) {
    const doc = document.createElement("template")
    doc.innerHTML = html
    const template = doc.content.querySelector("turbo-stream template")
    return template?.content.querySelector(".dashboard-goal-strip") ?? null
  }

  #dive(chip, target) {
    const from = centreOf(chip)
    const dx = target.x - from.x
    const dy = target.y - from.y
    return chip.animate([
      { transform: this.constructor.LIFT, opacity: 1 },
      { transform: `translate(${dx * 0.35}px, -52px) scale(1.1)`, offset: 0.35, easing: "cubic-bezier(0.55, 0, 0.9, 0.45)" },
      { transform: `translate(${dx}px, ${dy}px) scale(0.25)`, opacity: 0.7 }
    ], { duration: 460, easing: "cubic-bezier(0.3, 0.5, 0.6, 1)", fill: "forwards" }).finished
  }

  // Swap the server's strip in and play the rise on it. Swapped and offset in
  // the same task, so the new level is never painted before the animation.
  #land(goal, fresh, target) {
    const from = this.#levelOf(goal)
    const live = this.#swapAll(goal, fresh)
    const to = this.#levelOf(live)
    const liquid = live.querySelector(".goal-fill-liquid")
    const width = live.querySelector(".goal-fill")?.getBoundingClientRect().width ?? 0

    if (liquid && to > from) {
      const base = getComputedStyle(liquid).transform
      const own = base === "none" ? "" : base
      liquid.animate([
        { transform: `${own} translateX(${-((to - from) / 100) * width}px)` },
        { transform: own || "none" }
      ], { duration: this.constructor.FILL_MS, easing: this.constructor.EASE_FILL })
      this.#surgeWave(live)
    }

    live.querySelectorAll("[data-goal-strip-roll]").forEach((el) => rollDigits(el, true, { restore: true }))
    live.querySelector(".mascot-bob img")?.animate([
      { transform: "translateY(0) scale(1)" },
      { transform: "translateY(2px) scale(1.08, 0.9)", offset: 0.15 },
      { transform: "translateY(-16px) scale(0.96, 1.06)", offset: 0.45 },
      { transform: "translateY(0) scale(1.04, 0.96)", offset: 0.8 },
      { transform: "none" }
    ], { duration: 700, easing: "ease-out" })

    glow(target.x, target.y)
    sparks(target.x, target.y, 20)
    this.#haptic(this.landHapticTarget)
    return live
  }

  // Every copy of the goal strip (one per budget panel) takes the fresh one;
  // the copy in this rail is returned for the animation.
  #swapAll(goal, fresh) {
    let live = null
    document.querySelectorAll(".dashboard-goal-strip").forEach((strip) => {
      const copy = document.importNode(fresh, true)
      strip.replaceWith(copy)
      if (strip === goal) live = copy
    })
    return live
  }

  // The wave runs fast while the water rises, then back to its own pace.
  #surgeWave(strip) {
    const parts = strip.querySelectorAll(".goal-wave i, .goal-wave b")
    parts.forEach((el) => { el.style.animationDuration = "0.7s" })
    setTimeout(() => parts.forEach((el) => { el.style.animationDuration = "" }), this.constructor.FILL_MS + 100)
  }

  #levelOf(strip) {
    const value = strip?.querySelector(".goal-fill-liquid")?.style.getPropertyValue("--goal-pct")
    return parseFloat(value) || 0
  }

  // Just inside the water's moving edge, halfway down the strip.
  #waterEdge(strip) {
    const r = (strip.querySelector(".goal-fill-liquid") ?? strip).getBoundingClientRect()
    return { x: this.#rtl ? r.right - 16 : r.left + 16, y: r.top + r.height / 2 }
  }

  // Scrolls the rail by hand so the chip's timing is known; snapping is off
  // for the glide and back on once the goal sits at the snap point.
  #glideTo(goal) {
    const rail = this.element.parentElement
    const pad = parseFloat(getComputedStyle(rail).paddingInlineStart) || 0
    const railBox = rail.getBoundingClientRect()
    const box = goal.getBoundingClientRect()
    const delta = this.#rtl ? box.right - (railBox.right - pad) : box.left - (railBox.left + pad)
    if (Math.abs(delta) < 1) return Promise.resolve()

    const start = rail.scrollLeft
    const began = performance.now()
    rail.style.scrollSnapType = "none"
    return new Promise((resolve) => {
      const step = (now) => {
        const t = Math.min(1, (now - began) / this.constructor.GLIDE_MS)
        rail.scrollLeft = start + delta * (1 - Math.pow(1 - t, 4))
        if (t < 1) return requestAnimationFrame(step)
        rail.style.scrollSnapType = ""
        resolve()
      }
      requestAnimationFrame(step)
    })
  }

  #leave() {
    this.application.getControllerForElementAndIdentifier(this.element, "dismissible")?.dismiss()
  }

  #recover(chip) {
    chip?.remove()
    Turbo.visit(window.location.href, { action: "replace" })
  }

  #haptic(el) {
    this.application.getControllerForElementAndIdentifier(el, "bridge--haptic")?.tap()
  }

  #wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  get #goalStrip() {
    return this.element.parentElement?.querySelector(":scope > .dashboard-goal-strip") ?? null
  }

  get #rtl() {
    return getComputedStyle(this.element).direction === "rtl"
  }

  get #reducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }
}
